import React from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { Size } from 'components/DataTableComponent/DataTable.types';
import DialogModal from 'components/DialogModal/DialogModal';
import EditOfferDesignModal from 'components/EditOfferDesignModal/EditOfferDesignModal';
import PortalHeader from 'components/PortalHeader/PortalHeader';

import { IOffersFilterBarConfig } from '../OfferFilters.types';

import { OfferTableContent } from './OfferTableContent';
import { OffersFilterBar } from './OffersFilterBar';

interface IOfferPageLayoutProps {
  title: string;
  buttonText: string;
  onCreateClick: () => void;
  canUserEdit: boolean;
  canManageOffers: boolean;
  // Filters
  showFilters: boolean;
  customFilters?: React.ReactNode;
  offerLabel?: string;
  filterBarConfig?: Omit<IOffersFilterBarConfig, 'offerDesignType'>;
  hasActiveFilters?: boolean;
  // Table
  offers: Offer[];
  displayData: Offer[];
  columns: ColumnDef<Offer>[];
  totalCount: number;
  totalPages: number;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  emptyStateTitle: string;
  emptyStateDescription: string | React.ReactNode;
  createButtonPrefix: string;
  bodyDataTestId: string;
  emptyStateIcon?: React.ReactNode;
  tableContainerClassName?: string;
  tableSize?: Size;
  // Edit Design Modal
  offerType: OfferType;
  offerDesignType: OfferDesignTypes;
  selectedOffer: string | null;
  showLimitedEditModal: boolean;
  onCloseLimitedEditModal: () => void;
  // Delete Modal
  isDeleteDialogOpen: boolean;
  onCloseDeleteDialog: () => void;
  onConfirmDelete: () => void;
  deleteModalTitle: string;
  deleteModalDescription: string;
  // Extra content (e.g. Cheats dialog for Rolling Offers)
  children?: React.ReactNode;
}

export const OfferPageLayout = ({
  title,
  buttonText,
  onCreateClick,
  canUserEdit,
  canManageOffers,
  showFilters,
  customFilters,
  filterBarConfig,
  hasActiveFilters = false,
  offers,
  displayData,
  columns,
  totalCount,
  totalPages,
  rowsPerPage,
  currentPage,
  onPageChange,
  isLoading,
  emptyStateTitle,
  emptyStateDescription,
  createButtonPrefix,
  bodyDataTestId,
  emptyStateIcon,
  offerType,
  offerDesignType,
  selectedOffer,
  showLimitedEditModal,
  onCloseLimitedEditModal,
  isDeleteDialogOpen,
  onCloseDeleteDialog,
  onConfirmDelete,
  deleteModalTitle,
  deleteModalDescription,
  tableContainerClassName,
  tableSize,
  children
}: IOfferPageLayoutProps): React.ReactElement => {
  return (
    <div className="flex flex-col h-[calc(100vh-50px)]">
      <PortalHeader
        title={title}
        primaryButtonText={buttonText}
        onPrimaryButtonClick={onCreateClick}
        primaryButtonState={{
          disabled: !canUserEdit,
          hidden: !canManageOffers
        }}
        isCreateButton
      />
      {showFilters && customFilters}
      {!customFilters && (showFilters || hasActiveFilters) && (
        <div className="px-7 pb-4">
          <OffersFilterBar
            offerDesignType={offerDesignType}
            {...filterBarConfig}
          />
        </div>
      )}
      <div className="flex-1 overflow-y-scroll min-h-0">
        <OfferTableContent
          offers={offers}
          displayData={displayData}
          columns={columns}
          totalCount={totalCount}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          isLoading={isLoading}
          onCreateClick={onCreateClick}
          canCreate={canManageOffers}
          emptyStateTitle={emptyStateTitle}
          emptyStateDescription={emptyStateDescription}
          createButtonPrefix={createButtonPrefix}
          bodyDataTestId={bodyDataTestId}
          emptyStateIcon={emptyStateIcon}
          hasActiveFilters={hasActiveFilters}
          tableContainerClassName={tableContainerClassName}
          tableSize={tableSize}
        />
      </div>
      {showLimitedEditModal && (
        <EditOfferDesignModal
          offerId={selectedOffer || ''}
          offerDesignType={offerDesignType}
          onClose={onCloseLimitedEditModal}
          isOpen={showLimitedEditModal}
          offerType={offerType}
        />
      )}
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          title={deleteModalTitle}
          description={deleteModalDescription}
          modalActions={{
            onConfirm: { label: 'Delete', action: onConfirmDelete },
            onClose: {
              label: 'Cancel',
              action: onCloseDeleteDialog,
              variant: 'link'
            }
          }}
        />
      )}
      {children}
    </div>
  );
};
